import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import API from "@src/API.js";
import { Form, FormInput, FormActions } from "@src/components/Controls/Form";

function validationMessage(payload, fallback) {
  if (!payload) return fallback;
  if (typeof payload === "string") return payload;
  return payload.error || payload.message || payload.msg || fallback;
}

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  passwordConfirm: "",
};

export default function SmsJoin() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code") || "";
  const [lookupStatus, setLookupStatus] = useState("loading");
  const [maskedPhone, setMaskedPhone] = useState("");
  const [lookupError, setLookupError] = useState("");
  const [formData, updateFormData] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!code) {
        setLookupStatus("invalid");
        setLookupError("Missing sign-up link code.");
        return;
      }
      const resp = await API.getAsync(
        `/SmsJoinLookup?code=${encodeURIComponent(code)}`
      );
      if (cancelled) return;
      if (resp && resp.ok) {
        setMaskedPhone(resp.jsonData?.maskedPhone || "");
        setLookupStatus("ok");
      } else {
        setLookupStatus("invalid");
        setLookupError(
          validationMessage(
            resp?.jsonData,
            "This sign-up link is invalid or has expired."
          )
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code]);

  function isFilled(field) {
    return () => !!(formData[field] || "").toString().trim();
  }

  function isPwValid() {
    const password = formData.password || "";
    const confirm = formData.passwordConfirm || "";
    if (!password || !confirm) return false;
    if (password !== confirm) return false;
    if (password.length < 6) return false;
    return /^[a-zA-Z0-9!@#$%^&*_-]+$/.test(password);
  }

  function passwordInvalidMessage() {
    const password = formData.password || "";
    const confirm = formData.passwordConfirm || "";
    if (!password || !confirm) return "Password and confirmation are required";
    if (password.length < 6) return "Password must be at least 6 characters";
    if (!/^[a-zA-Z0-9!@#$%^&*_-]+$/.test(password)) {
      return "Passwords may contain only letters, numbers, or !@#$%^&*_-";
    }
    if (password !== confirm) return "Passwords do not match";
    return "Invalid password";
  }

  async function onSubmitAsync(event) {
    event.preventDefault();
    if (busy) return;
    setAttemptedSubmit(true);
    if (
      !isFilled("firstName")() ||
      !isFilled("lastName")() ||
      !isFilled("email")() ||
      !isPwValid()
    ) {
      return;
    }
    setBusy(true);
    try {
      const resp = await API.postAsync("/SmsJoinComplete", {
        code,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        password: formData.password,
        passwordConfirm: formData.passwordConfirm,
      });
      if (resp && resp.ok) {
        setCompleted(true);
        toast.success("Check your email to verify before signing in.");
      } else {
        toast.error(
          validationMessage(resp?.jsonData, "Unable to complete registration.")
        );
      }
    } finally {
      setBusy(false);
    }
  }

  if (lookupStatus === "loading") {
    return (
      <article className="mkt-page">
        <header className="mkt-hero">
          <h1>Complete your account</h1>
          <p data-testid="sms-join-status">Checking your sign-up link…</p>
        </header>
      </article>
    );
  }

  if (lookupStatus === "invalid") {
    return (
      <article className="mkt-page">
        <header className="mkt-hero">
          <h1>Sign-up link invalid</h1>
          <p data-testid="sms-join-status">{lookupError}</p>
        </header>
        <section className="mkt-section">
          <p>
            <Link to="/sms">Text JOIN to opt in</Link>
            {" · "}
            <Link to="/register">Register on the web</Link>
          </p>
        </section>
      </article>
    );
  }

  if (completed) {
    return (
      <article className="mkt-page">
        <header className="mkt-hero">
          <h1>Almost done</h1>
          <p data-testid="sms-join-status">
            Check your email to verify before signing in.
          </p>
        </header>
      </article>
    );
  }

  const afterAttempt = attemptedSubmit;

  return (
    <article className="mkt-page">
      <header className="mkt-hero">
        <h1>Complete your account</h1>
        <p>
          Phone <strong>{maskedPhone}</strong> is verified. Enter your details
          below.
        </p>
      </header>
      <section className="mkt-section">
        <Form
          className="login loginForm"
          label="Account details"
          object={formData}
          onUpdate={updateFormData}
        >
          <FormInput
            label="First Name"
            field="firstName"
            isValid={afterAttempt ? isFilled("firstName") : undefined}
            invalidMessage="Required"
          />
          <FormInput
            label="Last Name"
            field="lastName"
            isValid={afterAttempt ? isFilled("lastName") : undefined}
            invalidMessage="Required"
          />
          <FormInput
            label="Email"
            field="email"
            type="email"
            isValid={afterAttempt ? isFilled("email") : undefined}
            invalidMessage="Required"
          />
          <FormInput
            label="Password"
            field="password"
            type="password"
            isValid={afterAttempt ? isFilled("password") : undefined}
            invalidMessage="Required"
          />
          <FormInput
            label="Password Confirm"
            field="passwordConfirm"
            type="password"
            isValid={afterAttempt ? isPwValid : undefined}
            invalidMessage={passwordInvalidMessage()}
          />
          <FormActions
            doneLabel={busy ? "Creating account…" : "Create account"}
            onSaveAsync={onSubmitAsync}
          />
        </Form>
        <p style={{ marginTop: "1rem", fontSize: "0.9rem" }}>
          By creating an account you agree to receive CareerMaster account SMS
          alerts (verification codes and informational messages). Message
          frequency varies. Message and data rates may apply. Reply{" "}
          <strong>STOP</strong> to opt out. Reply <strong>HELP</strong> for
          help. See our <Link to="/privacy">Privacy Policy</Link> and{" "}
          <Link to="/terms">Terms</Link>.
        </p>
      </section>
    </article>
  );
}
